import React, { useMemo } from 'react';
import { useHistory } from 'react-router-dom';

import { useAppContext } from '@prisjakt/ui-app-context';

import { H3Text } from '@prisjakt/ui/text/variants';
import { NavigationSliceView } from 'src/areas/product-collection/components/navigation/navigation-component';

import { Section } from './styles';

import { trackSearchResultCategoryClicked } from '../../tracking';

import { TabAnchor } from '../navigation/types';
import {
  CategoryResults as CategoryResultsType,
  SearchFacetListItem,
  Maybe,
} from 'src/types';
import { FacetLink } from 'src/areas/product-collection/components/navigation/types';

type NewCategories = CategoryResultsType['nodes'] | undefined;
type OldCategories = Maybe<SearchFacetListItem>[];

type Props = {
  active: boolean;
  categories: NewCategories | OldCategories;
  count: number;

  // Experiment flags
  newSearch: boolean;
  wideContent: boolean;
  showAll?: boolean;
};

export const CategoryResults = (props: Props) => {
  const { categories, count, wideContent, newSearch, active, showAll } = props;

  const { t, market, linkTo } = useAppContext();
  const history = useHistory();

  const categoriesFromNewSearch = categories as NewCategories;
  const categoriesFromOldSearch = categories as OldCategories;

  const formattedLinks: FacetLink[] = useMemo(
    () =>
      !newSearch
        ? categoriesFromOldSearch?.map(category => {
            const imageUrl = category?.logo;
            const link: FacetLink = {
              id: category?.id || '',
              value: category?.name || '',
              selected: false,
              path: category?.url,
              imageUrl,
              indexable: true,
            };
            return link;
          }) ?? []
        : categoriesFromNewSearch?.map(category => {
            const link = {
              id: category?.id || '',
              value: category?.name || '',
              selected: false,
              path:
                category?.type === 'classic' && market.useClassicUrls
                  ? linkTo('category', category)
                  : category?.path,
              imageUrl: category?.image,
              indexable: true,
            };

            return link;
          }) ?? [],
    [
      categoriesFromNewSearch,
      categoriesFromOldSearch,
      newSearch,
      linkTo,
      market.useClassicUrls,
    ]
  );

  return categories?.length ? (
    <Section id={TabAnchor.CATEGORIES} $active={active}>
      <H3Text>{`${t('search', 'categories.title')} (${count})`}</H3Text>

      <NavigationSliceView
        links={formattedLinks}
        onClick={(link: FacetLink) => {
          if (link.path) {
            history.push(link.path);
            trackSearchResultCategoryClicked(link.id, link.value);
          }
        }}
        {...(wideContent && { itemLimit: 9 })}
        showAll={showAll}
      />
    </Section>
  ) : null;
};
